import Util from "./util";
var UtilObject = new Util();
const CART_CACHE_TTL_MS = 20 * 1000;
const CART_EXISTS_CACHE_TTL_MS = 20 * 1000;
const CART_LATEST_CACHE_TTL_MS = 20 * 1000;
const CART_429_COOLDOWN_MS = 1500;

const readSessionCache = (key) => {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const parsedValue = JSON.parse(rawValue);
    if (!parsedValue || Date.now() > parsedValue.expiresAt) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsedValue.data;
  } catch (error) {
    return null;
  }
};

const writeSessionCache = (key, data, ttlMs) => {
  try {
    const payload = {
      data,
      expiresAt: Date.now() + ttlMs,
    };
    sessionStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {}
};

const clearSessionCacheByPrefix = (prefix) => {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.indexOf(prefix) === 0) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {}
};

// Request tracker and result cache to prevent duplicate cart calls
const inflightCartRequests = {};
let lastCartCheckResult = null;
let lastCartCheckTime = 0;
const CART_CHECK_MIN_INTERVAL_MS = 1000;
let cart429CooldownUntil = 0;

let cartUpdateDebounceTimer = null;
const CART_UPDATE_DEBOUNCE_MS = 300;

class cartUtil {
  getCartCacheKey = () => {
    const allCookies = UtilObject.getAllCookies() || {};
    const customerId = allCookies.CUSTOMER_ID || "guest";
    const orderId = localStorage.getItem("ORDER_ID") || allCookies.ORDER_ID || "none";
    return `locaso:cache:cart:${customerId}:${orderId}`;
  };

  getCartExistsCacheKey = () => {
    const allCookies = UtilObject.getAllCookies() || {};
    const customerId = allCookies.CUSTOMER_ID || "guest";
    const orderId = localStorage.getItem("ORDER_ID") || allCookies.ORDER_ID || "none";
    return `locaso:cache:cart_exists:${customerId}:${orderId}`;
  };

  clearCartCaches = () => {
    lastCartCheckResult = null;
    lastCartCheckTime = 0;
    clearSessionCacheByPrefix("locaso:cache:cart:");
    clearSessionCacheByPrefix("locaso:cache:cart_exists:");
    clearSessionCacheByPrefix("locaso:cache:cart_latest:");
  };

  notifyCartUpdate = () => {
    if (cartUpdateDebounceTimer) {
      clearTimeout(cartUpdateDebounceTimer);
    }

    cartUpdateDebounceTimer = setTimeout(() => {
      this.clearCartCaches();
      document.dispatchEvent(new CustomEvent("cart-updated"));
      cartUpdateDebounceTimer = null;
    }, CART_UPDATE_DEBOUNCE_MS);
  };

  getLatestCartFromServer = () => {
    const cacheKey = "locaso:cache:cart_latest:" + (UtilObject.getCookie("CUSTOMER_ID") || "guest");
    const cachedLatestCart = readSessionCache(cacheKey);
    if (cachedLatestCart) {
      return Promise.resolve(cachedLatestCart);
    }

    return new Promise((resolve) => {
      if (Date.now() < cart429CooldownUntil) {
        resolve(null);
        return;
      }
      $.ajax({
        url: globalThis.baseURL + "api/customer/Cart/CartDetailsLatest",
        type: "GET",
        headers: { Authorization: globalThis.X_API_KEY },
        success: (result) => {
          writeSessionCache(cacheKey, result, CART_LATEST_CACHE_TTL_MS);
          resolve(result);
        },
        error: (err) => {
          if (err?.status === 429) {
            cart429CooldownUntil = Date.now() + CART_429_COOLDOWN_MS;
          }
          resolve(null);
        },
      });
    });
  };

  /**
   * Helper to generate a new random ORDER_ID via the server
   */
  _setRandomOrderId = () => {
    return new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/CustomerCookies/setRandomSessionId",
        type: "POST",
        data: { name: "ORDER_ID" },
        success: (result) => {
          this.clearCartCaches();
          resolve(result);
        },
        error: (err) => {
          resolve(null);
        },
      });
    });
  };

  cartCheck = async (forceRefresh = false) => {
    if (
      !forceRefresh &&
      Date.now() - lastCartCheckTime < CART_CHECK_MIN_INTERVAL_MS &&
      lastCartCheckResult
    ) {
      return lastCartCheckResult;
    }

    var allCookies = UtilObject.getAllCookies();

    if (allCookies.hasOwnProperty("CUSTOMER_ID")) {
      if (localStorage.getItem("ORDER_ID") != null) {
        // Local storage has ORDER_ID
        var cartValidStatus = await this.cart_exists_check();

        if (cartValidStatus == true) {
          await this.update_order_with_customer_id();
        } else {
          const latestCart = await this.getLatestCartFromServer();
          if (latestCart && latestCart.code != 18) {
            localStorage.setItem("ORDER_ID", latestCart.Order_ID);
            UtilObject.createCookie("ORDER_ID", latestCart.Order_ID, 30);
            this.clearCartCaches();
          } else {
            await this._setRandomOrderId();
            await this.cart_exists_check(true);
          }
        }
      } else {
        // No local storage ORDER_ID
        const latestCart = await this.getLatestCartFromServer();
        if (latestCart && latestCart.code != 18) {
          localStorage.setItem("ORDER_ID", latestCart.Order_ID);
          UtilObject.createCookie("ORDER_ID", latestCart.Order_ID, 30);
          this.clearCartCaches();
        } else {
          await this._setRandomOrderId();
          await this.cart_exists_check(true);
        }
      }
    } else {
      // User has not logged in
      if (allCookies.hasOwnProperty("ORDER_ID")) {
        var cartValidStatus = await this.cart_exists_check();
        if (cartValidStatus == false) {
          await this._setRandomOrderId();
        }
      } else {
        if (localStorage.getItem("ORDER_ID") == null) {
          await this._setRandomOrderId();
        } else {
          var localStorageOrderID = localStorage.getItem("ORDER_ID");
          UtilObject.createCookie("ORDER_ID", localStorageOrderID, 30);

          var cartValidStatus = await this.cart_exists_check();
          if (cartValidStatus == false) {
            await this._setRandomOrderId();
          }
        }
      }
    }

    var result = await this.get_cart(forceRefresh);
    lastCartCheckResult = result;
    lastCartCheckTime = Date.now();
    return result;
  };

  update_order_with_customer_id = () => {
    var localStorageOrderID = localStorage.getItem("ORDER_ID");
    const cacheKey = "locaso:request:set_customer_id:" + localStorageOrderID;

    if (inflightCartRequests[cacheKey]) {
      return inflightCartRequests[cacheKey];
    }

    const updatePromise = new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/Cart/SetCustomerIDToOrder",
        type: "POST",
        headers: { Authorization: globalThis.X_API_KEY },
        data: {
          LocalStorageOrderID: localStorageOrderID,
        },
        success: (result) => {
          this.clearCartCaches();
          resolve(result);
        },
        error: (err) => {
          resolve(null);
        },
        complete: () => {
          delete inflightCartRequests[cacheKey];
        },
      });
    });

    inflightCartRequests[cacheKey] = updatePromise;
    return updatePromise;
  };

  get_cart = (forceRefresh = false) => {
    const cacheKey = this.getCartCacheKey();
    if (!forceRefresh) {
      const cachedCart = readSessionCache(cacheKey);
      if (cachedCart) {
        return Promise.resolve(cachedCart);
      }
    }

    // Request Collapsing: If a request is already in flight for this key, return that same promise
    if (inflightCartRequests[cacheKey]) {
      return inflightCartRequests[cacheKey];
    }

    if (Date.now() < cart429CooldownUntil) {
      if (lastCartCheckResult) {
        return Promise.resolve(lastCartCheckResult);
      }
      return Promise.resolve({
        Products: [],
        Products_Not_Available: [],
        vendor: null,
        error: true,
      });
    }

    const cartPromise = new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/Cart/CartDetails",
        type: "GET",
        headers: { Authorization: globalThis.X_API_KEY },
        success: (result) => {
          let returnThis;
          if (result && result.code != 18) {
            returnThis = result;
          } else {
            returnThis = {
              Products: [],
              Products_Not_Available: [],
              vendor: null,
            };
          }
          writeSessionCache(cacheKey, returnThis, CART_CACHE_TTL_MS);
          resolve(returnThis);
        },
        error: (err) => {
          if (err?.status === 429) {
            cart429CooldownUntil = Date.now() + CART_429_COOLDOWN_MS;
            if (lastCartCheckResult) {
              resolve(lastCartCheckResult);
              return;
            }
          }
          resolve({
            Products: [],
            Products_Not_Available: [],
            vendor: null,
            error: true,
          });
        },
        complete: () => {
          // Remove from in-flight when done
          delete inflightCartRequests[cacheKey];
        },
      });
    });

    inflightCartRequests[cacheKey] = cartPromise;
    return cartPromise;
  };

  cart_exists_check = (forceRefresh = false) => {
    const cacheKey = this.getCartExistsCacheKey();
    if (!forceRefresh) {
      const cachedCartExists = readSessionCache(cacheKey);
      if (typeof cachedCartExists === "boolean") {
        return Promise.resolve(cachedCartExists);
      }
    }

    if (inflightCartRequests[cacheKey]) {
      return inflightCartRequests[cacheKey];
    }

    if (Date.now() < cart429CooldownUntil) {
      return Promise.resolve(false);
    }

    const existsPromise = new Promise((resolve) => {
      $.ajax({
        url: globalThis.baseURL + "api/customer/Cart/CartExistsCheck",
        type: "GET",
        headers: { Authorization: globalThis.X_API_KEY },
        success: (result) => {
          var CartExists = false;
          if (result && result.code != 18) {
            localStorage.setItem("ORDER_ID", result.Order_ID);
            CartExists = true;
          }
          writeSessionCache(cacheKey, CartExists, CART_EXISTS_CACHE_TTL_MS);
          resolve(CartExists);
        },
        error: (err) => {
          if (err?.status === 429) {
            cart429CooldownUntil = Date.now() + CART_429_COOLDOWN_MS;
          }
          resolve(false);
        },
        complete: () => {
          delete inflightCartRequests[cacheKey];
        },
      });
    });

    inflightCartRequests[cacheKey] = existsPromise;
    return existsPromise;
  };
}

const cartObjectInstance = new cartUtil();
export default cartObjectInstance;
export { cartUtil };
